import { types, type Instance } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';
import { TYPES, TYPE_INFO } from '../model/metamodel/mpc_config';

/** The editable change keys of an MPC change set (everything but `id`). */
type MPCChangeKey = 'type' | 'constrainedObjectName' | 'primaryPath' | 'secondaryPath';

const MultiplePropagationConstraintChanges = types
  .model('MultiplePropagationConstraintChanges', {
    id: types.string,
    type: types.maybe(types.enumeration(TYPES)),
    constrainedObjectName: types.maybe(types.string),
    primaryPath: types.maybe(types.string),
    secondaryPath: types.maybe(types.string),
  })
  .views(self => ({
    get dirty() {
      // We only allow to edit the type
      return self.type != null;
    },
  }))
  .actions(self => ({
    /**
     * Store a change for the given key.
     *
     * This action also receives the original value of the property and clears
     * the stored change if the new value for the change is the same as the
     * original value.
     */
    storeChange(key: MPCChangeKey, original: string | undefined, changed: string | undefined) {
      // Dynamic indexed write across keys of the same (string | undefined) type;
      // view the change props through a uniform record so the assignment types.
      const writable = self as unknown as Record<MPCChangeKey, string | undefined>;
      if (original !== changed) {
        writable[key] = changed;
      } else {
        writable[key] = undefined;
      }
    },
  }));

type IMPCChanges = Instance<typeof MultiplePropagationConstraintChanges>;

const MultiplePropagationConstraintInspectorChanges = types
  .model('MultiplePropagationConstraintInspectorChanges', {
    // the mpcs that have been removed
    removed: types.array(types.string),
    // the mpcs that may have pending changes
    changed: types.array(MultiplePropagationConstraintChanges),
    // the mpcs that are added
    added: types.array(MultiplePropagationConstraintChanges),
  })
  .views(self => ({
    // we are dirty if there are mpcs added or removed or there is an mpc in the
    // potentially changed list that is dirty
    get dirty() {
      return self.removed.length > 0 || self.changed.some(cs => cs.dirty) || self.added.length > 0;
    },
    // is an mpc removed?
    isRemoved(id: string) {
      return self.removed.includes(id);
    },
    // is an mpc dirty?
    isDirty(id: string) {
      return self.changed.some(c => c.id === id && c.dirty);
    },
    // are there pending changes for the property with the given key of the mpc
    // with the given id?
    hasChanged(id: string, key: MPCChangeKey) {
      return self.changed.some(c => c.id === id && c[key] !== null);
    },
    // is the mpc with the given id new?
    isNew(id: string) {
      return self.added.some(c => c.id === id);
    },
    // return the 'MultiplePropagationConstraintChanges' store for the mpc with
    // the given id
    changeSet(id: string) {
      return self.changed.find(c => c.id === id) || self.added.find(c => c.id === id);
    },
    // return all 'MultiplePropagationConstraintChanges' stores
    get changeSets() {
      return self.changed.concat(self.added);
    },
  }))
  .actions(self => ({
    discard() {
      // MST accepts an empty array to clear an array prop at runtime; the write
      // type is the concrete IMSTArray, so cast these resets (documented gap).
      self.removed = [] as unknown as typeof self.removed;
      self.changed = [] as unknown as typeof self.changed;
      self.added = [] as unknown as typeof self.added;
    },
    remove(id: string) {
      if (self.isNew(id)) {
        // `changeSet` returns possibly-undefined; legacy behaviour relies on it
        // being present here (guarded by `isNew`). Assert + cast to the concrete
        // instance for MST's `array.remove`.
        self.added.remove(self.changeSet(id) as unknown as IMPCChanges);
      } else {
        self.removed.push(id);
      }
    },
    undoRemove(id: string) {
      self.removed.remove(id);
    },
    addMPC() {
      const id = uuid();
      self.added.push({
        id,
        type: TYPE_INFO['EQUAL_TO'].key,
      });
      return id;
    },
    // remove the last added mpc
    undoAddMPC() {
      self.added = self.added.slice(0, -1) as unknown as typeof self.added;
    },
    /**
     * Store a change.
     *
     * Forwards to the appropriate 'MultiplePropagationConstraintChanges' store
     * or adds a new 'MultiplePropagationConstraintChanges' store to the list of
     * possibly changed mpcs.
     *
     * @param {*} id the mpc of the change
     * @param {*} key the changed property
     * @param {*} original the original value of the property
     * @param {*} changed the new value of the property
     */
    storeChange(
      id: string,
      key: MPCChangeKey,
      original: string | undefined,
      changed: string | undefined
    ) {
      const cs = self.changeSet(id);
      if (cs) {
        cs.storeChange(key, original, changed);
      } else if (original !== changed) {
        self.changed.push({
          id,
          [key]: changed,
        });
      }
    },
  }));

/**
 * UI state for a multiple propagation constraint inspector
 */
const MultiplePropagationConstraintInspector = types
  .model('MultiplePropagationConstraintInspector', {
    // the id of the mpc that is currently selected
    selected: types.maybeNull(types.string),
    // the id of the mpc that is currently being edited
    editing: types.optional(types.maybeNull(types.string), null),
    // are we editing a new mpc?
    adding: false,
    // the pending changes
    changes: types.optional(MultiplePropagationConstraintInspectorChanges, {}),
  })
  .views(self => ({
    get dirty() {
      return self.changes.dirty;
    },
    isEditing(id: string) {
      return self.editing === id;
    },
    // get an array with the ids of the removed mpcs
    get removals() {
      return self.changes.removed.slice();
    },
    // get an array with the properties of the mpc that changed
    get edits() {
      return self.changes.changed
        .filter(cs => cs.dirty)
        .map(cs => ({
          id: cs.id,
          type: cs.type,
        }));
    },
    // get an array with the added mpcs
    get additions() {
      return self.changes.added
        .filter(cs => cs.dirty)
        .map(cs => ({
          type: cs.type,
          primaryPath: cs.primaryPath != null ? cs.primaryPath.split('+') : undefined,
          secondaryPath: cs.secondaryPath != null ? cs.secondaryPath.split('+') : undefined,
        }));
    },
  }))
  .actions(self => {
    // `discardChanges` calls the sibling action `discardEditing` from this same
    // block; expose it through an interface alias so the forward reference types
    // (§4). Property writes stay on the raw `self`.
    const selfActions = self as unknown as { discardEditing(): void };
    return {
    clearSelections() {
      self.selected = null;
    },
    select(id: string, toggle = true) {
      if (toggle && self.selected === id) {
        self.selected = null;
      } else {
        self.selected = id;
      }
    },
    discardChanges() {
      self.changes.discard();
      selfActions.discardEditing();
    },
    discardEditing() {
      self.editing = null;
      self.adding = false;
    },
    startEdit(id: string, adding = false) {
      self.editing = id;
      self.adding = adding;
    },
    stopEdit() {
      self.editing = null;
      self.adding = false;
    },
    };
  });

export type IMultiplePropagationConstraintInspector = Instance<
  typeof MultiplePropagationConstraintInspector
>;

export default MultiplePropagationConstraintInspector;
